// File: frontend/src/components/Dashboard/RecentActivities.jsx
// Recent activities component

import React from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneIcon,
  EnvelopeIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ClockIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

const RecentActivities = ({ data, userId, userScope }) => {
  const activities = data || [];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'call':
        return PhoneIcon;
      case 'email':
        return EnvelopeIcon;
      case 'qualification':
        return CheckCircleIcon;
      case 'opportunity':
        return TrophyIcon;
      case 'update':
      default:
        return UserPlusIcon;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'call':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'email':
        return 'bg-green-50 text-green-600 border-green-200';
      case 'qualification':
        return 'bg-yellow-50 text-yellow-600 border-yellow-200';
      case 'opportunity':
        return 'bg-purple-50 text-purple-600 border-purple-200';
      case 'update':
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const activityTime = new Date(dateString);
    const diffInHours = Math.floor((now - activityTime) / (1000 * 60 * 60));
    
    if (diffInHours === 0) return 'Agora';
    if (diffInHours === 1) return '1 hora atrás';
    if (diffInHours < 24) return `${diffInHours} horas atrás`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1 dia atrás';
    if (diffInDays < 7) return `${diffInDays} dias atrás`;
    
    return activityTime.toLocaleDateString('pt-BR');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Atividades Recentes
      </h3>
      
      {activities.length > 0 ? (
        <div className="space-y-4">
          {activities.slice(0, 5).map((activity, index) => {
            const IconComponent = getActivityIcon(activity.type);
            
            return (
              <div key={activity.id || index} className="flex items-start space-x-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                <div className={`p-2 rounded-lg border ${getActivityColor(activity.type)}`}>
                  <IconComponent className="h-4 w-4" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {activity.description}
                  </div>
                  
                  <div className="flex items-center space-x-2 mt-1">
                    {activity.user && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        por {activity.user}
                      </span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      • {formatTimeAgo(activity.time)}
                    </span>
                  </div>
                  
                  {activity.status && (
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        activity.status === 'completed' ? 'bg-green-100 text-green-800' :
                        activity.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {activity.status === 'completed' ? 'Concluído' :
                         activity.status === 'pending' ? 'Pendente' : activity.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium">Nenhuma atividade recente</p>
            <p className="text-sm mt-1">As atividades aparecerão aqui conforme você trabalha com leads</p>
          </div>
        </div>
      )}
      
      {activities.length > 5 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Link to="/activities" className="text-sm text-blue-600 hover:text-blue-500 font-medium">
            Ver todas as atividades →
          </Link>
        </div>
      )}
    </div>
  );
};

export default RecentActivities;